// Right-side expandable drawer — Captain pivot 2026-04-23: "make
// the right drawer always openable please, make it push content
// over rather than float, and have it the height of content too."
//
// The Drawer now participates in the tab layout rather than floating
// over it: when open it occupies a column on the right, content
// pushes left to make room. Height matches the tab content area
// (whatever the flex parent allows). Always-openable — the handle
// is visible even when the tab has no drawer content.

import { ChevronLeft, ChevronRight } from "lucide-react";
import type { ReactNode } from "react";

interface Props {
  open: boolean;
  children: ReactNode;
  testId?: string;
  /** Width in px when the drawer is open. Default 384 (w-96). */
  width?: number;
}

export function Drawer({ open, children, testId, width = 384 }: Props) {
  return (
    <>
      {/* Drawer body — inline column that pushes tab content left
       * when open. Sibling of the scrollable tab content inside a
       * flex-row container. Header was removed 2026-04-24 — the
       * per-message label inside the drawer's chat already carries
       * the seat/model identity, so a top header box was redundant.
       * The close affordance lives on the DrawerHandle. */}
      {open && (
        <aside
          className="flex h-full flex-col bg-transparent"
          style={{ width }}
          data-testid={testId}
        >
          <div className="flex-1 overflow-hidden">{children}</div>
        </aside>
      )}
    </>
  );
}

/**
 * Drawer toggle handle — separate from the body so it can sit inline
 * at the content's right edge even when the drawer is collapsed.
 * Rendered as a thin strip with a chevron; click to toggle.
 */
export function DrawerHandle({
  open,
  onToggle,
  title,
  testId,
}: {
  open: boolean;
  onToggle: () => void;
  title: string;
  testId?: string;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={open ? `Close ${title}` : `Open ${title}`}
      title={title}
      className="flex h-full w-3 cursor-pointer items-center justify-center bg-transparent text-muted transition-colors hover:text-primary"
      data-testid={testId}
    >
      {open ? <ChevronRight size={10} /> : <ChevronLeft size={10} />}
    </button>
  );
}
